<template>
  <vue-phone-number-input
    v-model="phoneNumber.phone"
    :default-country-code="phoneNumber.countryCode"
    :use-default-country-code-as-placeholder="true"
    :error="error"
    color="#2563eb"
    error-color="#ef4444"
    valid-color="#10b981"
    :show-code-on-list="true"
    @update="onPhoneNumberInputUpdated"
  />
</template>

<script>
import VuePhoneNumberInput from 'vue-phone-number-input'

import { getCountryCallingCode, isValidNumber, parseNumber, parsePhoneNumber } from 'libphonenumber-js'
import constData from '@/helpers/const-data'

export default {
  components: {
    VuePhoneNumberInput
  },
  props: {
    value: {
      type: String, // e164 format
      default: null
    },
    required: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    phoneNumber: {
      isValid: false,
      e164: '',
      countryCode: null,
      countryCallingCode: null,
      nationalNumber: null,
      phone: ''
    }
  }),

  computed: {
    error() {
      return !this.phoneNumber.phone ? this.required : !this.phoneNumber.isValid
    },

    currentValueIsReset() {
      return this.valueIsReset(this.value)
    }
  },

  watch: {
    'phoneNumber.phone': {
      handler() {
        this.detectCountryCodeFromNumber()
      },
      immediate: true
    },
    value: {
      handler(newValue, oldValue) {
        const newValueIsReset = this.valueIsReset(newValue)
        const oldValueIsReset = this.valueIsReset(oldValue)

        if (newValue === this.phoneNumber.e164 || (newValueIsReset && oldValueIsReset)) {
          return
        }

        if (newValueIsReset && !oldValueIsReset) {
          this.resetPhoneNumber()
        } else {
          this.parsePhoneNumberFromString(newValue)
        }
      },
      immediate: true
    }
  },

  methods: {
    valueIsReset(value) {
      return value === undefined || value === null
    },

    detectCountryCodeFromNumber() {
      const { phone } = this.phoneNumber

      if (!phone || !phone.startsWith('+')) return

      try {
        const numberStr = phone.startsWith('+') ? phone : `+${phone}`
        const phoneNumber = parsePhoneNumber(numberStr)

        if (phoneNumber && phoneNumber.country) {
          this.phoneNumber.countryCode = phoneNumber.country
        }
      } catch (error) {}
    },

    resetPhoneNumber() {
      this.phoneNumber = {
        isValid: false,
        e164: '',
        countryCode: null,
        countryCallingCode: null,
        nationalNumber: null,
        phone: ''
      }
    },

    getCountryFromE164(phoneNumber) {
      if (typeof phoneNumber !== 'string' || !phoneNumber.trim()) {
        return { name: 'United States', code: 'US', countryCallingCode: '+1' }
      }

      const cleaned = phoneNumber.replace(/[^+\d]+/g, '')

      let candidate = constData.countries.find(country => cleaned.startsWith(country.countryCallingCode))

      if (candidate) return candidate

      if (/^\d/.test(cleaned)) {
        const withPlus = '+' + cleaned
        candidate = constData.countries.find(country => withPlus.startsWith(country.countryCallingCode))
        if (candidate) return candidate
      }

      return { name: 'United States', code: 'US', countryCallingCode: '+1' }
    },

    parsePhoneNumberFromString(src) {
      const parsedNumber = parseNumber(src)
      const numberIsValid = isValidNumber(parsedNumber)
      if (!numberIsValid) {
        const country = this.getCountryFromE164(src)

        this.phoneNumber = {
          isValid: false,
          e164: src,
          countryCode: country.code,
          countryCallingCode: country.countryCallingCode,
          nationalNumber: src.replace(country.countryCallingCode, ''),
          phone: src
        }

        console.warn(`Invalid number passed to NamedPhoneNumberInput component: ${src}`)
        return
      }

      this.phoneNumber = {
        isValid: numberIsValid,
        e164: src,
        countryCode: parsedNumber.country,
        countryCallingCode: getCountryCallingCode(parsedNumber.country),
        nationalNumber: parsedNumber.phone,
        phone: parsedNumber.phone
      }
    },

    onPhoneNumberInputUpdated(updatedNumber) {
      const country = this.getCountryFromE164(updatedNumber.e164)

      this.phoneNumber = {
        isValid: updatedNumber.isValid,
        e164: updatedNumber.e164,
        countryCode: country.code ?? updatedNumber.countryCode,
        countryCallingCode:
          country.countryCallingCode ??
          updatedNumber.countryCallingCode ??
          getCountryCallingCode(updatedNumber.countryCode),
        nationalNumber: updatedNumber.nationalNumber || null,
        phone: updatedNumber.phone
      }

      this.$emit('input', updatedNumber.e164 || null)
    }
  },
  mounted() {
    this.detectCountryCodeFromNumber()
  }
}
</script>
